import * as Sentry from "@sentry/react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { authenticateConnectUser } from "@/app/connect/auth";
import type { ConnectProviderContext } from "@/app/connect/auth/types";
import { Sidebar } from "@/app/connect/components";
import { InsightsBanner } from "@/app/connect/components/InsightsBanner";
import { MobileConnectDrawer } from "@/app/connect/components/MobileConnectDrawer";
import { MobileTopBar } from "@/app/connect/components/MobileTopBar";
import { PageLoader } from "@/components/ui/page-loader";
import { isChunkLoadError, reloadForChunkError } from "@/lib/chunkReload";

// Cache the auth result to avoid re-authenticating on every child navigation
let cachedProvider: ConnectProviderContext | null = null;

export const Route = createFileRoute("/connect")({
	pendingComponent: () => <PageLoader message="Loading Amber Connect..." />,
	beforeLoad: async ({ location }) => {
		// If already authenticated, reuse cached context
		if (cachedProvider) {
			return { connectProvider: cachedProvider };
		}

		// Check for agent access (provider_id in URL)
		const searchParams = new URLSearchParams(location.searchStr || "");
		const providerIdFromUrl = searchParams.get("provider_id");

		// Run the full auth flow
		const result = await authenticateConnectUser(providerIdFromUrl);

		if (!result.success) {
			window.location.href = result.redirectTo || "/list";
			throw new Error("AUTH_REDIRECT");
		}

		// Cache the result
		cachedProvider = result.context!;
		return { connectProvider: result.context };
	},
	errorComponent: ({ error }) => {
		if (error?.message === "AUTH_REDIRECT") {
			return <PageLoader message="Redirecting..." />;
		}
		if (isChunkLoadError(error) && reloadForChunkError()) {
			return <PageLoader message="Updating…" />;
		}
		Sentry.captureException(error);
		return (
			<div className="flex min-h-screen flex-col items-center justify-center">
				<p className="text-gray-500">Something went wrong. Please refresh.</p>
			</div>
		);
	},
	component: ConnectLayout,
});

function ConnectLayout() {
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Load Freshworks widget only on Connect pages
	useEffect(() => {
		// Set widget config
		(window as any).fwSettings = { widget_id: 84000003090 };

		// Create stub if not already loaded
		if (typeof (window as any).FreshworksWidget !== "function") {
			const n: any = (...args: any[]) => {
				n.q.push(args);
			};
			n.q = [];
			(window as any).FreshworksWidget = n;
		}

		// Load the script
		const script = document.createElement("script");
		script.src = "https://ind-widget.freshworks.com/widgets/84000003090.js";
		script.async = true;
		script.defer = true;
		document.body.appendChild(script);

		// Add style to move widget above pagination
		const style = document.createElement("style");
		style.textContent = `#freshworks-container iframe[id="launcher-frame"] { bottom: 80px !important; }`;
		document.head.appendChild(style);

		return () => {
			// Cleanup on unmount (navigating away from Connect)
			document.body.removeChild(script);
			document.head.removeChild(style);
			const container = document.getElementById("freshworks-container");
			if (container) container.remove();
			delete (window as any).FreshworksWidget;
			delete (window as any).fwSettings;
		};
	}, []);

	return (
		<div className="flex h-dvh flex-col bg-background text-foreground">
			<InsightsBanner />
			{/* Mobile top bar — only visible below lg */}
			<MobileTopBar onMenuClick={() => setDrawerOpen(true)} />
			{/* Mobile drawer */}
			<MobileConnectDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
			<div className="flex min-h-0 flex-1">
				{/* Desktop sidebar — hidden below lg */}
				<div className="hidden lg:flex">
					<Sidebar />
				</div>
				<main className="flex-1 overflow-hidden">
					<Outlet />
				</main>
			</div>
		</div>
	);
}
